'use client';
import { CaptchaFormMessage } from '@eliadmin/core/src/components/providers/captcha-form-message';

import { resolveImageAlt } from '@eliadmin/core/src/utils/image-alt-utils';

import type { Product } from '@eliadmin/core/src/api/slugs/types/slug-detail-types';
import {
  ResponsiveDialog,
  ResponsiveDialogContent,
  ResponsiveDialogFooter,
  ResponsiveDialogHeader,
  ResponsiveDialogTitle,
} from '@eliadmin/core/src/components/ui/responsive-dialog';
import { Button } from '@eliadmin/core/src/components/ui/button';
import {
  Form,
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormMessage,
} from '@eliadmin/core/src/components/ui/form';
import { Input } from '@eliadmin/core/src/components/ui/input';
import { Textarea } from '@eliadmin/core/src/components/ui/textarea';
import { Checkbox } from '@eliadmin/core/src/components/ui/checkbox';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { useFormValidationMessages } from '@eliadmin/core/src/hooks/use-form-validation-messages';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { GDPRTextContactForm } from '@eliadmin/core/src/utils/gdpr-tos/gdpr-text-contact-form';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { useLocaleStore } from '@eliadmin/core/src/stores/locale-store';
import Image from '@eliadmin/core/src/components/image/image-wrapped';
import { useCustomerStore } from '@eliadmin/core/src/stores/customer-store';
import { PhoneInputField } from '@eliadmin/core/src/components/ui/phone-input/phone-input';
import { sendContactMessage } from '@eliadmin/core/src/api/forms/forms-api';

import type { ProductDetailData } from '@eliadmin/core/src/api/products/types/product-types';
import { Field } from '../../form/labeled-field';

type AskForPriceDialogProps = {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  product: Product | ProductDetailData;
};

export const AskForPriceDialog = ({ isOpen, setIsOpen, product }: AskForPriceDialogProps) => {
  const { t } = useTranslations();
  const { formValidationMessages } = useFormValidationMessages(t);
  const locale = useLocaleStore(s => s.currentLocale);
  const { me } = useCustomerStore();

  // 1) build Zod schema
  const askPriceSchema = z.object({
    name: z.string().min(1, formValidationMessages.CANT_BE_EMPTY_VALIDATION_MESSAGE()),
    email: z.string().email(formValidationMessages.EMAIL_INVALID_VALIDATION_MESSAGE()),
    phone: z.string().optional(),
    quantity: z.string().min(1, formValidationMessages.CANT_BE_EMPTY_VALIDATION_MESSAGE()),
    company_buy: z.boolean().optional(),
    note: z.string().optional(),
    gdpr: z
      .boolean()
      .refine(val => val === true, { message: formValidationMessages.GDPR_VALIDATION_MESSAGE() }),
  });

  type AskPriceForm = z.infer<typeof askPriceSchema>;

  // 2) setup RHF
  const form = useForm<AskPriceForm>({
    resolver: zodResolver(askPriceSchema),
    defaultValues: {
      name: me?.customer?.name ? `${me?.customer?.name} ${me?.customer?.surname}` : '',
      email: me?.customer?.email || '',
      phone: me?.customer?.phone || '',
      quantity: '1',
      company_buy: false,
      note: '',
      gdpr: false,
    },
  });

  const onSubmit = form.handleSubmit(async data => {
    try {

      const contactBody = {
        subject: `${t('text2278')} - ${product.name}`,
        email: data.email,
        name: data.name,
        phone: data.phone,
        text: data.note,
        custom: {
          text22: data.quantity,
          text361: data.company_buy ? t('text1417') : t('text1551'),
        },
        gdpr: data.gdpr,

      };

      sendContactMessage({
        data: contactBody,
        locale,
      });
    } catch (err) {
      // eslint-disable-next-line no-console
      if (process.env.NODE_ENV === 'development') console.log(err);
    }
  });

  return (
    <ResponsiveDialog open={isOpen} onOpenChange={setIsOpen}>
      <ResponsiveDialogContent
        overlayClassName="z-[99998] bg-black/80 backdrop-blur-none"
        dialogProps={{
          className:
            'z-[99999] max-w-screen max-h-[94vh] w-[680px] overflow-y-auto sm:max-w-[600px] md:max-w-[680px] lg:max-w-[680px]',
        }}
        drawerProps={{
          className: 'z-[99999] max-h-[94vh]',
        }}
      >
        <div className="flex flex-col overflow-auto">
          <ResponsiveDialogHeader className="flex flex-col items-start justify-start gap-6">
            <ResponsiveDialogTitle className="font-oswald text-2xl font-medium uppercase text-black sm:text-3xl lg:text-4xl">
              {t('text2278')}
            </ResponsiveDialogTitle>
            <div className="flex flex-col items-start gap-2 md:flex-row md:items-center">
              <Image
                src={product?.image?.url ?? '/images/no-image.png'}
                alt={resolveImageAlt(product?.image, product?.name)}
                width={100}
                height={100}
                className="object-cover object-center"
              />

              <h2 className="font-oswald text-lg font-bold uppercase text-black sm:text-xl lg:text-2xl">
                {product.name}
              </h2>
            </div>
          </ResponsiveDialogHeader>

          <Form {...form}>
        <CaptchaFormMessage errors={form.formState.errors} />
            <form
              onSubmit={onSubmit}
              className={cn('flex flex-col gap-4', 'ea-ask-for-price__dialog-form')}
            >
              <Field
                required
                name="name"
                placeholder={t('text33')}
                label={t('text33')}
                control={form.control}
                className={{ wrapper: 'bg-white' }}
              />
              <Field
                required
                name="email"
                placeholder={t('text35')}
                label={t('text35')}
                type="email"
                control={form.control}
                className={{ wrapper: 'bg-white' }}
              />
              <FormField
                control={form.control}
                name={'phone'}
                render={({ field }) => (
                  <FormItem className="relative max-h-[67px] min-h-[67px] w-full rounded-none p-[1px]">
                    <FormControl>
                      <div className="relative flex max-h-[67px] min-h-[67px] items-end justify-end rounded-none">
                        <FormLabel
                          htmlFor={'phone'}
                          className={cn(
                            'absolute left-3 top-0 z-[5] m-0 flex h-[30px] items-center justify-center bg-white p-1 text-xs font-normal text-black',
                          )}
                          isRequired={false}
                        >
                          {t('text36')}
                        </FormLabel>

                        <div
                          className={cn(
                            'h-[54px] w-full overflow-hidden rounded-[4px] border border-border bg-white',
                          )}
                        >
                          <PhoneInputField
                            placeholder={t('text36')}
                            {...(field as any)}
                            variant="default"
                            className="h-[54px] w-full"
                            inputClassName="h-[54px] bg-white text-base"
                            inputStyle={{
                              height: '54px',
                              width: '100%',
                              border: 'none',
                              paddingLeft: '20px',
                              fontSize: '16px',
                              lineHeight: '20px',
                              borderRadius: 0,
                              backgroundColor: 'transparent',
                            }}
                            countrySelectorStyleProps={{
                              buttonClassName: 'bg-white',
                              buttonStyle: {
                                height: '54px',
                                width: '58px',
                                paddingLeft: '12px',
                                border: 'none',
                                borderRadius: 0,
                                backgroundColor: 'transparent',
                              },
                            }}
                          />
                        </div>
                      </div>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <Field
                required
                name="quantity"
                placeholder={t('text22')}
                label={t('text22')}
                control={form.control}
                className={{ wrapper: 'bg-white' }}
              />
              <FormField
                control={form.control}
                name="company_buy"
                render={({ field }) => (
                  <FormItem className="flex flex-row items-center space-x-2">
                    <FormControl>
                      <Checkbox checked={field.value} onCheckedChange={field.onChange} />
                    </FormControl>
                    <FormLabel>{t('text361')}</FormLabel>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <Field
                name="note"
                placeholder={t('text57')}
                label={t('text57')}
                control={form.control}
                type="textarea"
                className={{ wrapper: 'bg-white' }}
              />
              <FormField
                control={form.control}
                name="gdpr"
                render={({ field }) => (
                  <FormItem
                    className={cn(
                      'col-span-12 flex items-start',
                      'ea-contact-form__gdpr-checkbox-item ea-contact-form__checkbox-item',
                    )}
                  >
                    <FormControl>
                      <Checkbox
                        ref={field.ref}
                        name={field.name}
                        disabled={field.disabled}
                        checked={field.value}
                        onCheckedChange={field.onChange}
                        className={cn(
                          '',
                          'ea-contact-form__gdpr-checkbox ea-contact-form__checkbox',
                        )}
                      />
                    </FormControl>
                    <FormLabel className={cn('pt-[3px]', 'ea-contact-form__gdpr-checkbox-label')}>
                      <GDPRTextContactForm locale={locale} />
                    </FormLabel>
                  </FormItem>
                )}
              />

              <ResponsiveDialogFooter className="flex flex-row justify-between">
                <Button
                  variant="dark"
                  onClick={() => setIsOpen(false)}
                  type="button"
                  className={'w-full uppercase'}
                >
                  {t('text377')}
                </Button>
                <Button type="submit" className={'w-full uppercase'}>
                  {t('text185')}
                </Button>
              </ResponsiveDialogFooter>
            </form>
          </Form>
        </div>
      </ResponsiveDialogContent>
    </ResponsiveDialog>
  );
};
